import React from 'react';
import { useTranslation } from 'react-i18next';
import { USER_ROLES, type UserRole, type UserStatus } from '../types';

interface UserToolbarProps {
  activeRole?: UserRole;
  activeStatus?: UserStatus;
  searchValue: string;
  canCreate: boolean;
  onRoleChange: (role?: UserRole) => void;
  onStatusChange: (status?: UserStatus) => void;
  onSearchChange: (value: string) => void;
  onCreate: () => void;
}

const UserToolbar: React.FC<UserToolbarProps> = ({
  activeRole,
  activeStatus,
  searchValue,
  canCreate,
  onRoleChange,
  onStatusChange,
  onSearchChange,
  onCreate,
}) => {
  const { t } = useTranslation();
  const roleOptions: Array<{ value?: UserRole; label: string }> = [
    { label: t('admin.users.filters.all') },
    ...USER_ROLES.map((role) => ({
      value: role,
      label: t(`admin.users.roles.${role.toLowerCase()}`),
    })),
  ];

  return (
    <div className="border-b border-outline-variant bg-white px-3 py-3 sm:px-5 sm:py-4">
      <div className="flex gap-1 overflow-x-auto pb-2 sm:pb-3" role="tablist" aria-label={t('admin.users.filters.role')}>
        {roleOptions.map((option) => {
          const isActive = activeRole === option.value;
          return (
            <button
              key={option.value ?? 'ALL'}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`min-h-9 shrink-0 rounded-lg px-3 font-label-sm text-label-sm transition-colors ${isActive ? 'bg-primary text-on-primary' : 'text-secondary hover:bg-surface-container-low'}`}
              onClick={() => onRoleChange(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(180px,240px)_minmax(260px,1fr)_auto]">
        <label className="sr-only" htmlFor="user-status-filter">{t('admin.users.filters.status')}</label>
        <select
          id="user-status-filter"
          className="h-11 min-w-0 rounded-xl border border-outline-variant bg-white px-3 text-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
          value={activeStatus ?? ''}
          onChange={(event) => {
            const nextStatus = event.target.value;
            onStatusChange(nextStatus ? (nextStatus as UserStatus) : undefined);
          }}
        >
          <option value="">{t('admin.users.filters.all_statuses')}</option>
          <option value="ACTIVE">{t('admin.users.status.active')}</option>
          <option value="INACTIVE">{t('admin.users.status.inactive')}</option>
        </select>

        <label className="relative min-w-0">
          <span className="sr-only">{t('admin.users.filters.search_label')}</span>
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[20px] text-outline">search</span>
          <input
            type="search"
            className="h-11 w-full rounded-xl border border-outline-variant bg-white pl-10 pr-3 text-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            placeholder={t('admin.users.filters.search_placeholder')}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </label>

        {canCreate && (
          <button
            type="button"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary shadow-sm hover:bg-on-primary-fixed-variant"
            onClick={onCreate}
          >
            <span className="material-symbols-outlined text-[20px]">person_add</span>
            {t('admin.users.actions.create')}
          </button>
        )}
      </div>
    </div>
  );
};

export default UserToolbar;
